/** @prettier */
import React from "react";
import { Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const assertNever = (_arg: never) => null;

// Map from a bootstrap style to the necessary color to make the icon match
const iconColorForBootstrapStyle = {
    info: "#17a2b8",
    warning: "#f0ad4e",
    danger: "#d9534f",
    success: "#5cb85c",
};

type QuestionViewerModalProps = {
    promptText: string;
    explanationText: string;
    confirmButtonText: string;
    cancelButtonText: string;
    bsStyle: keyof typeof iconColorForBootstrapStyle;
    onConfirm: () => void;
    onCancel: () => void;
};
/* Component for a single modal */
const QuestionViewerModal = ({
    promptText,
    explanationText,
    confirmButtonText,
    cancelButtonText,
    bsStyle,
    onConfirm,
    onCancel,
}: QuestionViewerModalProps) => (
    <Modal bsSize="sm" show={true} onHide={onCancel}>
        <Modal.Body className="qv-floating-dialog">
            <div
                className="qv-floating-dialog-icon"
                style={{ color: iconColorForBootstrapStyle[bsStyle] }}
            >
                <FontAwesomeIcon
                    size="3x"
                    icon="exclamation-triangle"
                ></FontAwesomeIcon>
            </div>
            <div className="qv-floating-dialog-prompt-text">{promptText}</div>
            <div className="qv-floating-dialog-text">{explanationText}</div>
        </Modal.Body>
        <Modal.Footer>
            <Button bsStyle={bsStyle} onClick={onConfirm}>
                {confirmButtonText}
            </Button>
            <Button
                className="cancel-delete-button"
                bsStyle="link"
                onClick={onCancel}
            >
                {cancelButtonText}
            </Button>
        </Modal.Footer>
    </Modal>
);

export type QuestionViewerModalState =
    | "confirmDelete"
    | "confirmDiscard"
    | "confirmSubmitNewVersion"
    | "confirmPublishNewVersion"
    | "saveSubmittedAsDraft"
    | "saveAsNewVersionDraft";

type QuestionViewerModalsProps = {
    state: QuestionViewerModalState;
    isNew: boolean;
    isPublished: boolean;
    hasPrevVersion: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};
export const QuestionViewerModals = ({
    state,
    isNew,
    isPublished,
    hasPrevVersion,
    onConfirm,
    onCancel,
}: QuestionViewerModalsProps) => {
    return state === "confirmDelete" ? (
        <QuestionViewerModal
            promptText={"Delete?"}
            explanationText={
                hasPrevVersion
                    ? isPublished
                        ? "Current published question will be deleted.  No prior published question will be active"
                        : "Unpublished version of question will be deleted.  Current published vesrion remains active."
                    : "Question will be deleted."
            }
            confirmButtonText={"Delete"}
            cancelButtonText={"Cancel"}
            bsStyle={"danger"}
            onConfirm={onConfirm}
            onCancel={onCancel}
        />
    ) : state === "confirmDiscard" ? (
        isNew ? (
            <QuestionViewerModal
                promptText={"Discard Draft?"}
                explanationText={
                    "Draft will be discarded. Question never existed."
                }
                confirmButtonText={"Discard Draft"}
                cancelButtonText={"Keep editing"}
                bsStyle={"danger"}
                onConfirm={onConfirm}
                onCancel={onCancel}
            />
        ) : (
            <QuestionViewerModal
                promptText={"Discard changes?"}
                explanationText={
                    "Unsaved changes will be discarded. Question will remain in current state."
                }
                confirmButtonText={"Discard changes"}
                cancelButtonText={"Keep editing"}
                bsStyle={"danger"}
                onConfirm={onConfirm}
                onCancel={onCancel}
            />
        )
    ) : state === "saveSubmittedAsDraft" ? (
        <QuestionViewerModal
            promptText="Revert to draft?"
            explanationText={
                "This will return the question to the Draft state. " +
                "The question will no longer be submitted for review."
            }
            confirmButtonText="Save as Draft"
            cancelButtonText="Keep editing"
            bsStyle="warning"
            onConfirm={onConfirm}
            onCancel={onCancel}
        ></QuestionViewerModal>
    ) : state === "saveAsNewVersionDraft" ? (
        <QuestionViewerModal
            promptText="Save as new version Draft?"
            explanationText={
                "This action will create a new version of the question in the Draft state. " +
                "Current published version will remain active until new version is published."
            }
            confirmButtonText="Save as Draft"
            cancelButtonText="Keep editing"
            bsStyle="warning"
            onConfirm={onConfirm}
            onCancel={onCancel}
        ></QuestionViewerModal>
    ) : state === "confirmSubmitNewVersion" ? (
        <QuestionViewerModal
            promptText="Submit new version?"
            explanationText={
                "This action will create a new version of the question in the Submitted state. " +
                "Current published version will remain active until new version is published."
            }
            confirmButtonText="Submit"
            cancelButtonText="Keep editing"
            bsStyle="info"
            onConfirm={onConfirm}
            onCancel={onCancel}
        ></QuestionViewerModal>
    ) : state === "confirmPublishNewVersion" ? (
        <QuestionViewerModal
            promptText="Publish new version?"
            explanationText={
                "This question version will replace the current question version in the QBank as the active version. "
            }
            confirmButtonText="Submit"
            cancelButtonText="Keep editing"
            bsStyle="success"
            onConfirm={onConfirm}
            onCancel={onCancel}
        ></QuestionViewerModal>
    ) : (
        assertNever(state)
    );
};
